'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2022 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** Pipe type
//***********************************************************************************
//***********************************************************************************

import { fh_add, fh_mul, fh_polygon, fh_solid } from '@acenv/fh-3d-viewer';
import { cn_element_type } from './cn_element_type';
import { cn_configuration, cn_configuration_choice, cn_configuration_line, cn_configuration_param, cn_configuration_param_group, cn_configuration_tab } from './cn_configuration';
import { cn_constant, PIPE_EXTRA_MATERIAL_LIST, PIPE_FLUID_LIST, PIPE_MATERIAL_LIST, PIPE_SHAPE_LIST } from './cn_model_constants';
import { cn_element_type_visitor } from '..';

/**
 *
 * @class cn_pipe_type
 * Common class type for pipes
 */
export class cn_pipe_type extends cn_element_type {
    //***********************************************************************************
    /**
     * Constructor
     */
    constructor(parent) {
        super(parent);
        this.class_name = 'cn_pipe_type';
        this.name = '';

        this.thickness = 0.2;
        this.width = 0.2;

        this.fluid = 'water'
        this.material = 'pvc';
        this.shape = 'circular';
    }

    //***********************************************************************************
    /**
     * Creates a default pipe type
     * @returns {cn_pipe_type[]}
     */
    static default_types(parent) {
        const def = [];

        const copper_pipe_10 = new cn_pipe_type(parent);
        copper_pipe_10.thickness = 0.01;
        copper_pipe_10.fluid = 'water';
        copper_pipe_10.material = 'copper';
        copper_pipe_10.shape = 'circular';
        def.push(copper_pipe_10);

        const pvc_pipe_50 = new cn_pipe_type(parent);
        pvc_pipe_50.thickness = 0.05;
        pvc_pipe_50.fluid = 'water';
        pvc_pipe_50.material = 'pvc';
        pvc_pipe_50.shape = 'circular';
        def.push(pvc_pipe_50);

        const pvc_unknown = new cn_pipe_type(parent);
        pvc_unknown.thickness = 0.1;
        pvc_unknown.fluid = 'waste';
        pvc_unknown.material = 'pvc';
        pvc_unknown.shape = 'circular';
        def.push(pvc_unknown);

        return def;
    }

    //***********************************************************************************
    /**
     * Returns a clone of this
     * @returns {cn_pipe_type}
     */
    clone() {
        const c = new cn_pipe_type(this.parent);
        c.name = this.name;
        c.fluid = this.fluid;
        c.material = this.material;
        c.shape = this.shape;
        c.width = this.width;
        c.thickness = this.thickness;

        return c;
    }

    //***********************************************************************************
    get_generic_label() {
        return 'Type de conduit';
    }

    //***********************************************************************************
    /**
     * Returns the list of keys of the element
     * @returns {string[]}
     */
    model_keys() {
        return ['name', 'fluid', 'material', 'shape', 'width', 'thickness'];
    }

    //***********************************************************************************
    /**
     * Serialize
     * @returns {object}
     */
    serialize() {
        const json = this.serialize_element();
        json.class_name = 'cn_pipe_type';
        json.name = this.name;
        json.fluid = this.fluid;
        json.material = this.material;
        json.shape = this.shape;
        json.width = this.width;
        json.thickness = this.thickness

        return json;
    }

    //***********************************************************************************
    /**
     * Unserialize
     * @param {object} json
     * @returns {cn_pipe_type | false}
     */
    static unserialize(json, parent) {
        const ot = new cn_pipe_type(parent);
        ot.unserialize_element(json);
        ot.name = json.name;
        ot.thickness = json.thickness || json.diameter;

        if (typeof (json.fluid) == 'string' && cn_constant.code_to_label(json.fluid, PIPE_FLUID_LIST))
            ot.fluid = json.fluid;

        if (typeof (json.material) == 'string') {
            if (cn_constant.code_to_label(json.material, PIPE_MATERIAL_LIST) || cn_constant.code_to_label(json.material, PIPE_EXTRA_MATERIAL_LIST))
                ot.material = json.material;
            else
                ot.material = "other";
        }

        ot.shape = json.shape || 'circular';
        ot.width = json.width || 0.2;

        return ot;
    }

    get_material_label() {
        return cn_constant.code_to_label(this.material, PIPE_MATERIAL_LIST) || cn_constant.code_to_label(this.material, PIPE_EXTRA_MATERIAL_LIST) || "Autre";
    }

    //***********************************************************************************
    /**
     * Returns a displayble label
     * @returns {string}
     */
    get_label() {
        if (this.name !== '') return this.name;
        const html = cn_constant.code_to_label(this.fluid, PIPE_FLUID_LIST) + ' - ' + this.get_material_label();
        const size = this.shape === 'circular' ? `⌀ ${(this.thickness * 1000).toFixed(0)} mm` : `${(this.thickness * 1000).toFixed(0)}x${(this.width * 1000).toFixed(0)}`
        return `${html} ${size}`;
    }

    //***********************************************************************************
    /**
     * Returns an array of objects describing the type.
     * @returns {{label: string, value?: any, decimals?: number, unit?: string}[]}
     */
    get_description() {
        const description = [];

        const fluid_label = cn_constant.code_to_label(this.fluid, PIPE_FLUID_LIST);
        if (fluid_label)
            description.push({ label: 'Fluide', value: fluid_label });

        const material_label = this.get_material_label();
        if (material_label)
            description.push({ label: 'Matériau', value: material_label });

        const shape_label = cn_constant.code_to_label(this.shape, PIPE_SHAPE_LIST);
        if (shape_label) {
            description.push({ label: 'Forme', value: shape_label })
        }

        if (this.shape === 'circular') {
            description.push({ label: 'Diamètre', value: this.thickness * 1000, decimals: 0, unit: 'mm' });
        } else if (this.shape === 'rectangular') {
            description.push({ label: 'Largeur', value: this.width * 1000, decimals: 0, unit: 'mm' });
            description.push({ label: 'Épaisseur', value: this.thickness * 1000, decimals: 0, unit: 'mm' });
        }

        return description;
    }

    //***********************************************************************************
    /**
     * Returns a html string to be put in a <svg>
     * @param {number} w : width of svg element
     * @param {number} h : height of svg element
     * @returns {string}
     */
    draw_svg_icon(w, h) {
        let html = ``;

        const od = this.thickness;
        const scale = (1 > w / h) ? w / (1.05 * od) : h / (1.05 * od);

        const x0 = w / 2 - od * scale / 2;
        const x1 = w / 2 + od * scale / 2;
        const y0 = h / 2 - od * scale / 2;
        const y1 = h / 2 + od * scale / 2;

        //html += `<ellipse cx="${(x0 + x1) * 0.5}" cy="${(y0 + y1) * 0.5}" rx="${0.5 * (x1 - x0)}" ry="${0.5 * (y1 - y0)}" `;

        let fill_material = cn_constant.code_to_rgbcolor(this.material,PIPE_MATERIAL_LIST);
        let fill_fluid = cn_constant.code_to_rgbcolor(this.fluid,PIPE_FLUID_LIST);

        if (this.shape === 'circular') {
            const ar = 0.25 * (x1 - x0);
            const alpha = ar * Math.cos(Math.PI * 0.25);
            html += `<path d="M ${x0 + ar - alpha} ${y0 + ar + alpha} `;
            html += `A  ${ar} ${ar} 0 1 1 ${x0 + ar + alpha} ${y0 + ar - alpha} `;
            html += `L ${x1 - ar + alpha} ${y1 - ar - alpha}`;
            html += `A  ${ar} ${ar} 0 1 1 ${x1 - ar - alpha} ${y1 - ar + alpha} `;
            html += `Z"`;
            html += ` class="pipe" fill="${fill_material}"/>`;
            html += `<circle cx="${x1 - ar}" cy="${y1 - ar}" r="${ar}" class="pipe"  fill="${fill_material}" />`;
            html += `<circle cx="${x1 - ar}" cy="${y1 - ar}" r="${ar * 0.8}" class="pipe" fill="${fill_fluid}" />`;
        } else if (this.shape === 'rectangular') {
            const ar = 0.33 * (x1 - x0);
            const alpha = ar * (1 + Math.cos(Math.PI * 0.25));
            const xa = 0.1 * (x1 - x0)
            const xb = xa + ar
            const ya = 0.1 * (y1 - y0)
            const yb = ya + ar
            html += `<path d="M ${xa} ${ya} `;
            html += `L ${xa} ${yb} `;
            html += `L ${xa + alpha} ${yb + alpha} `;
            html += `L ${xa + alpha} ${ya + alpha} `;
            html += `Z" class="pipe" fill="${fill_material}" />`;
            html += `<path d="M ${xa} ${ya} `;
            html += `L ${xb} ${ya} `;
            html += `L ${xb + alpha} ${ya + alpha} `;
            html += `L ${xb + alpha} ${yb + alpha} `;
            html += `L ${xa + alpha} ${yb + alpha} `;
            html += `L ${xa + alpha} ${ya + alpha} Z"`;
            html += ` class="pipe" fill="${fill_material}" />`;
            html += `<rect x="${xa + alpha}" y="${ya + alpha}" width="${ar}" height="${ar}" class="pipe" fill="${fill_material}" />`;
            html += `<rect x="${xa + alpha + ar * 0.1}" y="${ya + alpha + ar * 0.1}" width="${ar * 0.8}" height="${ar * 0.8}" class="pipe" fill="${fill_fluid}" />`;
        }
        return html;

    }

    //***********************************************************************************
    //**** Build 3D solid
    //***********************************************************************************
    /**
     * Builds a 3D solid with origin (0,0,0)
     * @param {number} height
     */
    build_solid(height) {
        const solid = new fh_solid();
        if (this.shape === 'circular') {
            const dz = [0, 0, height];
            const dx = [this.thickness, 0, 0];
            const dy = [0, this.thickness, 0];
            const ori = [-0.5 * this.thickness, -0.5 * this.thickness, 0];
            solid.cylinder(ori, dx, dy, dz);
        } else if (this.shape === 'rectangular') {
            const dz = [0, 0, height];
            const dx = [this.width, 0, 0];
            const dy = [0, this.thickness, 0];
            const ori = [0, 0, 0];
            solid.brick(ori, dx, dy, dz);
        }

        solid['color'] = this.get_color();

        return solid;
    }

    //***********************************************************************************
    /**
     * Build footprint, with left corner at 0,0, as horizontal polygon
     * @returns {fh_polygon}
     */
    build_footprint() {
        const dz = [0, 0, 1];
        const dx = [this.thickness, 0, 0];
        const dy = [0, this.thickness, 0];
        const ori = [-0.5 * this.thickness, -0.5 * this.thickness, 0];
        const polygon = new fh_polygon(ori, dz);
        const contour = [];
        const center = [this.thickness / 2, this.thickness / 2, 0];
        for (let theta = 0; theta < 360; theta += 10) {
            const x = 0.5 * Math.cos(theta * Math.PI / 180);
            const y = 0.5 * Math.sin(theta * Math.PI / 180);
            contour.push(fh_add(center, fh_add(fh_mul(dx, x), fh_mul(dy, y))));
        }
        polygon.add_contour(contour);
        return polygon;
    }

    //***********************************************************************************
    /*** Returns color */
    get_color() {
        if (this.material === 'pvc')
            return [100 / 255, 100 / 255, 100 / 255];
        if (this.material === 'copper')
            return [150 / 255, 100 / 255, 100 / 255];
        if (this.material === 'per')
            return [150 / 255, 100 / 255, 150 / 255];
        return [100 / 255, 150 / 255, 100 / 255];
    }

    //***********************************************************************************
    //**** Get configuration
    //***********************************************************************************
    static configuration() {
        const configuration = new cn_configuration();

        //*** Global params

        const param_group = new cn_configuration_param_group('Dimensions', 'dimensions');
        configuration.add_param_group(param_group);
        param_group.add_param(new cn_configuration_param('Épaisseur', 'thickness', 5, 500, 'mm'));
        param_group.add_param(new cn_configuration_param('Largeur', 'width', 5, 500, 'mm'));

        const tab = new cn_configuration_tab('Typologie', 'standard');
        configuration.add_tab(tab);
        this.prepare_configuration_tab(tab);

        return configuration;
    }

    //***********************************************************************************
    //**** Prepare material and shape configuration for tabs
    //***********************************************************************************
    static prepare_configuration_tab(tab) {
        //*** Shape
        const shape_list = new cn_configuration_line('Forme', 'shape');
        tab.add_line(shape_list);
        PIPE_SHAPE_LIST.forEach((shape) => {
            shape_list.add_choice(new cn_configuration_choice(shape.label, shape.code));
        });

        //*** Fluid
        const fluid_list = new cn_configuration_line('Fluide', 'fluid');
        tab.add_line(fluid_list);
        PIPE_FLUID_LIST.forEach((fluid) => {
            fluid_list.add_choice(new cn_configuration_choice(fluid.label, fluid.code));
        });

        //*** Material
        const material_list = new cn_configuration_line('Matériau', 'material');
        tab.add_line(material_list);
        PIPE_MATERIAL_LIST.forEach((material) => {
            material_list.add_choice(new cn_configuration_choice(material.label, material.code));
        });
        if (PIPE_EXTRA_MATERIAL_LIST.length && !material_list.get_param("extra_material"))
        {
            material_list.add_param(cn_configuration_param.choice_list("Matériau","extra_material",PIPE_EXTRA_MATERIAL_LIST.map(m => m.label),0));
        }
    }

    //***********************************************************************************
    //**** Fill configuration
    //***********************************************************************************
    /**
     * Returns a new configuration built from the element type, or fills an existing configuration.
     * @param {cn_configuration} config
     * @returns {cn_configuration}
     */
    fill_configuration(config = null) {
        const configuration = (config) ? config : cn_pipe_type.configuration();
        // tab selected
        const selection = configuration.selection;
        const param_group = configuration.get_param_group('dimensions');
        param_group.set_param('thickness', this.thickness * 1000);

        const shape_line = configuration.tabs[selection].get_line('shape');
        shape_line.set_selection(this.shape);

        if (this.shape === 'circular') {
            param_group.set_param('width', this.width * 1000, false);
        } else if (this.shape === 'rectangular') {
            param_group.set_param('width', this.width * 1000 || 200, true);
        }

        const fluid_line = configuration.tabs[selection].get_line('fluid');
        fluid_line.set_selection(this.fluid);

        const material_line = configuration.tabs[selection].get_line('material');
        if (cn_constant.code_to_label(this.material,PIPE_MATERIAL_LIST))
        {
            material_line.set_selection(this.material);
            material_line.set_param("extra_material",0,false);
        }
        else if (cn_constant.code_to_label(this.material,PIPE_EXTRA_MATERIAL_LIST))
        {
            material_line.set_selection("other");
            material_line.set_param("extra_material",PIPE_EXTRA_MATERIAL_LIST.findIndex(m =>m.code == this.material),true);
        }

        return configuration;
    }

    //***********************************************************************************
    //**** Load configuration
    //***********************************************************************************
    load_configuration(configuration, actual_change = false) {
        const param_group = configuration.get_param_group('dimensions');
        this.thickness = param_group.get_param('thickness').value / 1000;
        this.width = param_group.get_param('width').value / 1000;

        // tab selected
        const selection = configuration.selection;
        const configuration_tab = configuration.tabs[selection];
        this.shape = configuration_tab.get_line('shape').get_selection();
        this.fluid = configuration_tab.get_line('fluid').get_selection();
        let material_line = configuration_tab.get_line('material');
        this.material = material_line.get_selection();
        let extra_material = material_line.get_param("extra_material");
        if (this.material == "other" && extra_material)
            this.material = PIPE_EXTRA_MATERIAL_LIST[extra_material.value].code;
        return true;
    }

    /**
     * Accept element type visitor
     *
     * @param {cn_element_type_visitor} element_type_visitor
     */
    accept_visitor(element_type_visitor) {
        element_type_visitor.visit_pipe_type(this);
    }
}

